<template>
  <div class="nav-fields">
    <div class="header-selector header-item">
      <div class="label-icon">
        <p v-if="apiExpertUrl || !projectOrgId">Collection:</p>
        <p v-else>Organization:</p>
        <router-link :to="{ name: 'settings-organization', params: { orgId: projectOrgId } }">
          <i class="glyphicon glyphicon-cog cog" />
        </router-link>
      </div>
      <Select2
        v-model="pickedOrganization"
        :options="orgPickerOptions"
        :settings="orgPickerSettings"
        class="top-bar-select"
      />
    </div>
    <div class="header-selector header-item">
      <div class="label-icon">
        <p>Project:</p>
        <router-link :to="{ name: 'settings-project' }">
          <i class="glyphicon glyphicon-cog cog" />
        </router-link>
      </div>
      <Select2
        v-model="pickedProject"
        :options="projPickerOptions"
        :settings="projPickerSettings"
        class="top-bar-select"
      />
    </div>
  </div>
</template>

<script>
import { call, get } from 'vuex-pathify';
import Select2 from './Select2';

const userTemplate = (text) => {
  // eslint-disable-next-line no-undef
  return $(`<i class="fa fa-user" style="padding-right: 8px;"></i><span>${text}</span>`);
};

const favouriteTemplate = (text) => {
  // eslint-disable-next-line no-undef
  return $(`<i class="fa fa-star" style="padding-right: 8px;"></i><span>${text}</span>`);
};

export default {
  name: 'ProjectPicker',
  components: {
    Select2,
  },
  data() {
    return {
      pickedProject: '',
      pickedOrganization: '',
      projPickerSettings: {
        placeholder: 'Select a project...',
        allowEmpty: true,
        templateSelection: (item) => {
          if (item.id === '_') {
            return userTemplate(item.text);
          }
          if (this.favProjects?.find((fav) => fav === item.id?.toLowerCase())) {
            return favouriteTemplate(item.text);
          }
          return item.text;
        },
        templateResult: (item) => {
          if (item.id === '_') {
            return userTemplate(item.text);
          }
          if (this.favProjects?.find((fav) => fav === item.id?.toLowerCase())) {
            return favouriteTemplate(item.text);
          }
          return item.text;
        },
      },
      orgPickerSettings: {
        placeholder: 'Select a collection...',
        allowEmpty: true,
        templateSelection: (item) => {
          if (item.id === '_') {
            return userTemplate(item.text);
          }
          if (this.favOrgs?.find((fav) => fav === item.id?.toLowerCase())) {
            return favouriteTemplate(item.text);
          }
          return item.text;
        },
        templateResult: (item) => {
          if (item.id === '_') {
            return userTemplate(item.text);
          }
          if (this.favOrgs?.find((fav) => fav === item.id?.toLowerCase())) {
            return favouriteTemplate(item.text);
          }
          return item.text;
        },
      },
    };
  },
  computed: {
    orgPickerOptions() {
      const getText = ({ name }) => name;
      const getOpt = (org) => ({ id: org.id, text: getText(org) });

      const orgs = Object.values(this.organizations)
        .map((org) => getOpt(org))
        .sort((a, b) => a.text.localeCompare(b.text));
      return [...orgs, { id: '_', text: 'Personal Projects' }];
    },
    projPickerOptions() {
      const opts = [];
      const getText = ({ name }) => name;
      const getOption = ({ project: { id, name } }) => ({ id, text: getText({ id, name }) });

      const projList = Object.values(this.projects);
      const projFilter =
        this.pickedOrganization && this.pickedOrganization !== '_'
          ? ({ project }) => project.org_id === this.pickedOrganization
          : ({ project }) => !project.org_id;
      const projects = projList.filter(projFilter);

      const owned = projects
        .filter((proj) => proj.access_level === 'OWNER')
        .sort((a, b) => (a.project?.name || '').localeCompare(b.project?.name));
      if (owned.length) {
        opts.push({
          text: 'Owner access:',
          children: owned.map((project) => getOption(project)),
        });
      }

      const edit = projects
        .filter((proj) => proj.access_level === 'EDITOR')
        .sort((a, b) => (a.project?.name || '').localeCompare(b.project?.name));
      if (edit.length) {
        opts.push({
          text: 'Edit access:',
          children: edit.map((project) => getOption(project)),
        });
      }
      const analyst = projects
        .filter((proj) => proj.access_level === 'ANALYST')
        .sort((a, b) => (a.project?.name || '').localeCompare(b.project?.name));
      if (analyst.length) {
        opts.push({
          text: 'Analyst access:',
          children: analyst.map((project) => getOption(project)),
        });
      }
      const view = projects
        .filter((proj) => proj.access_level === 'VIEWER')
        .sort((a, b) => (a.project?.name || '').localeCompare(b.project?.name));
      if (view.length) {
        opts.push({
          text: 'View access:',
          children: view.map((project) => getOption(project)),
        });
      }

      return opts;
    },
    apiExpertUrl() {
      if (this.projectId && this.projectOrgId) {
        const orgTag = (this.projectOrgTags || []).find((o) => o.startsWith('api_expert:'));
        if (orgTag) {
          const urlFrag = orgTag.replace('api_expert:', '');
          return `https://www.api.expert/collection/${urlFrag}/`;
        }
      }
      return null;
    },
    organizations: get('account2/organizations'),
    projects: get('account2/projects'),
    projectId: get('account2/projectId'),
    projectOrgId: get('account2/projects[:projectId].project.org_id'),
  },
  watch: {
    '$auth.isAuthenticated': function _isLoggedIn(isAuthenticated) {
      if (!isAuthenticated) {
        this.pickedProject = '';
        this.pickedOrganization = '';
      }
    },
    projectsLoaded(val) {
      if (val) {
        if (this.projectId) {
          this.pickedProject = this.projectId;
          this.pickedOrganization = this.projectOrgId || '_';
        }
      }
    },
    async pickedProject(id) {
      if (id && (id !== this.projectId || this.$route.meta.noProjectOk)) {
        await this.setProject({ id });
        // If we're viewing a route with an param, it's project specific
        // so go to the home page
        // Or if it's not project specific, go to the project home
        if (Object.keys(this.$route.params).length || this.$route.meta.noProjectOk) {
          this.$router.push({ name: 'project-home' });
        }
      }
    },
    projectId(val) {
      if (val) {
        this.$nextTick(() => {
          this.pickedProject = val;
          this.fetchData();
        });
      }
    },
    projectOrgId(val) {
      this.pickedOrganization = val || '_';
    },

    $route(to) {
      if (to.meta.noProjectOk) {
        this.pickedProject = '';
        this.pickedOrganization = '';
      }
      if (!to.meta.noProjectOk && this.projectId) {
        this.pickedProject = this.projectId;
        this.pickedOrganization = this.projectOrgId || '_';
      }
    },
  },
  methods: {
    async fetchData() {
      if (this.projectId) {
        if (this.projectOrgId) {
          this.pickedOrganization = this.projectOrgId;
        } else {
          this.pickedOrganization = '_';
        }
        this.pickedProject = this.projectId;
      }
    },
    setProject: call('account2/setProject'),
  },
};
</script>

<style scoped>
.cog {
  color: var(--light-primary);
}
.cog:hover {
  color: var(--accent);
}

.header-selector {
  display: flex;
  flex-direction: column;
  width: 250px;
  gap: 8px;
}

.label-icon {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.span-text,
.label-icon p {
  margin: 0;
  font-size: 14px;
}

.nav-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
}

@media (max-width: 767px) {
  .header-item {
    flex: 1;
    flex-basis: 100%;
  }
  .header-selector {
    width: 100%;
  }
}

@media (min-width: 768px) {
  .nav-fields {
    flex-direction: row;
  }
}
</style>
